import { Plugin, type Editor } from 'ckeditor5/src/core';
import { UpcastWriter } from 'ckeditor5/src/engine';
import type { ClipboardPipeline } from 'ckeditor5/src/clipboard';
import { cleanPastedStyles, cleanHtmlString } from './utils';
import type { CleanPasteConfig } from './cleanpasteconfig';

/**
 * The clean paste editing plugin.
 *
 * Handles the actual cleaning of pasted content by intercepting
 * clipboard events and processing the content.
 */
export default class CleanPasteEditing extends Plugin {
	/**
	 * @inheritDoc
	 */
	public static get requires() {
		return [ 'ClipboardPipeline' ] as const;
	}

	/**
	 * @inheritDoc
	 */
	public static get pluginName() {
		return 'CleanPasteEditing' as const;
	}

	/**
	 * @inheritDoc
	 */
	public init(): void {
		const editor = this.editor;
		const clipboardPipeline = editor.plugins.get( 'ClipboardPipeline' ) as ClipboardPipeline;

		const config: CleanPasteConfig = {
			removeColors: true,
			removeBackgroundColors: true,
			removeFontFamilies: true,
			removeLineHeights: true,
			removeMargins: false,
			removePadding: false,
			preserveListStyles: true,
			preserveTableStyles: true,
			...( editor.config.get( 'cleanPaste' ) || {} )
		};

		clipboardPipeline.on( 'inputTransformation', ( evt, data ) => {
			this._cleanViewContent( data.content, editor, config );
		}, { priority: 'high' } );

		editor.editing.view.document.on( 'clipboardInput', ( evt, data ) => {
			const htmlData = data.dataTransfer.getData( 'text/html' );

			if ( htmlData ) {
				const cleanedHtml = cleanHtmlString( htmlData, config );

				try {
					data.dataTransfer.setData( 'text/html', cleanedHtml );
				} catch ( e ) {
					console.warn( 'CleanPaste: Could not modify clipboard data', e );
				}
			}
		}, { priority: 'high' } );
	}

	/**
	 * Cleans the view content recursively.
	 */
	private _cleanViewContent( content: any, editor: Editor, config: CleanPasteConfig ): void {
		const writer = new UpcastWriter( editor.editing.view.document );

		// Recursively clean all elements in the content
		for ( const child of content.getChildren() ) {
			this._cleanViewElement( child, writer, config );
		}
	}

	/**
	 * Cleans a single view element and its children.
	 */
	private _cleanViewElement( element: any, writer: UpcastWriter, config: CleanPasteConfig ): void {
		if ( !element.is || !element.is( 'element' ) ) {
			return;
		}

		const elementName = element.name.toLowerCase();
		const isListItem = [ 'li', 'ul', 'ol' ].indexOf( elementName ) !== -1;
		const isTable = [ 'table', 'td', 'th', 'tr', 'thead', 'tbody' ].indexOf( elementName ) !== -1;

		const attributesToRemove: Array<string> = [];

		for ( const [ name, value ] of element.getAttributes() ) {
			if ( this._shouldRemoveAttribute( name, value ) ) {
				attributesToRemove.push( name );
			} else if ( name === 'style' ) {
				const shouldPreserve =
					( config.preserveListStyles && isListItem ) ||
					( config.preserveTableStyles && isTable );

				const cleanedStyle = cleanPastedStyles( value as string, config, shouldPreserve );

				if ( cleanedStyle !== value ) {
					if ( cleanedStyle ) {
						writer.setAttribute( 'style', cleanedStyle, element );
					} else {
						attributesToRemove.push( name );
					}
				}
			}
		}

		for ( const attr of attributesToRemove ) {
			writer.removeAttribute( attr, element );
		}

		for ( const child of element.getChildren() ) {
			this._cleanViewElement( child, writer, config );
		}
	}

	/**
	 * Determines if an attribute should be removed.
	 */
	private _shouldRemoveAttribute( name: string, value: string ): boolean {
		const unwantedAttributes = [
			'data-mce-style',
			'data-mce-bogus',
			'data-mce-fragment',
			'data-mce-href',
			'data-paste-word',
			'xmlns',
			'xml:space'
		];

		if ( unwantedAttributes.indexOf( name ) !== -1 ) {
			return true;
		}

		if ( name.startsWith( 'data-' ) && !name.startsWith( 'data-ck' ) ) {
			return true;
		}

		if ( name === 'class' ) {
			const allowedClassPatterns = [
				/^ck-/,
				/^smart-button/,
				/^note-/
			];

			const classList = value.split( /\s+/ );
			const hasOnlyAllowedClasses = classList.every( cls =>
				allowedClassPatterns.some( pattern => pattern.test( cls ) )
			);

			return !hasOnlyAllowedClasses && classList.length > 0;
		}

		return false;
	}
}
